import { cleanUpCachedFetchedData, localOrMemoryStorage, sessionOrMemoryStorage, updateObject } from '@vista-digital/utilities';
import { action, autorun, observable } from 'mobx';
import { deepMerge } from '../utils/object-utils';
import { DomainStoreType } from './types/domain-store';
import { createStorageCache } from './utils/create-storage-cache';
function createDomainStore(props) {
    switch (props.type) {
        case DomainStoreType.BrowserLocal:
        case DomainStoreType.BrowserSession:
            return createPersistedDomainStore(props);
        case DomainStoreType.Memory:
            return createMemoryDomainStore(props);
        default:
            throw new Error(`Cannot create domain store with unknown type ${props.type}.`);
    }
}
export { createDomainStore };
/**
 * Creates a domain store with a hydrate method that retrieves and stores data to browser storage.
 */
function createPersistedDomainStore(props) {
    var _a;
    const { defaultState, type } = props;
    const storageCache = createPersistedStorageCache(props);
    // Gets the cached data from the storage cache and cleans up any expired fetched data
    const getCachedData = () => {
        const cachedData = storageCache.getValue();
        return cachedData ? cleanUpCachedFetchedData(cachedData, defaultState) : undefined;
    };
    // Set the data based on the cached value, or sets it to the default state
    const data = observable.object((_a = getCachedData()) !== null && _a !== void 0 ? _a : defaultState);
    // Update the data, allowing for deep merging of nested partial data
    const setState = action((newData) => updateObject(data, deepMerge(data, newData)));
    // Reset data at root level
    const reset = action(() => updateObject(data, defaultState));
    // Automatically persist the store when the data changes
    autorun(() => storageCache.setValue(data));
    return {
        type,
        data,
        reset,
        hydrate: () => {
            const cachedData = getCachedData();
            if (cachedData)
                setState(cachedData);
        },
        setState
    };
}
function createPersistedStorageCache(props) {
    const { name, type } = props;
    switch (type) {
        case DomainStoreType.BrowserSession:
            return createStorageCache(sessionOrMemoryStorage, name);
        case DomainStoreType.BrowserLocal:
            return createStorageCache(localOrMemoryStorage, name);
        default:
            throw new Error(`Cannot create persisted storage cache. Unsupported DomainStoreType: '${type}'.`);
    }
}
/**
 * Creates a domain store that is only available during execution time, not making use of browser storage.
 */
function createMemoryDomainStore(props) {
    const { defaultState, type } = props;
    const data = observable.object(defaultState);
    // Update the data, allowing for deep merging of nested partial data
    const setState = action((newData) => updateObject(data, deepMerge(data, newData)));
    // Reset data at root level
    const reset = action(() => updateObject(data, defaultState));
    return {
        type,
        data,
        reset,
        setState
    };
}
//# sourceMappingURL=create-domain-store.js.map